import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
// SignInGate.tsx — nothing renders until we know who is looking.
//
// The flow is Anvil's App.tsx gate: ask the server who is signed in, show the login panel until it
// answers with someone. There is no open mode — a host with no client id configured still shows the
// panel, and the panel says the server cannot sign anyone in. That state is a misconfiguration to be
// read off the screen, never a way through.
//
// Google Identity Services is loaded from its script rather than through @react-oauth/google (which
// is what Anvil uses): this client's only dependencies are react and react-dom, and a sign-in button
// is not worth a package + lockfile churn when GIS is ~30 lines of the same thing. The client id
// comes from the server at runtime, so one committed bundle serves every environment.
import { Component } from "react";
import { fetchAuthConfig, fetchAuthState, signInWithGoogle } from "./AuthApi";
const GIS_SRC = "https://accounts.google.com/gsi/client";
export class SignInGate extends Component {
    constructor() {
        super(...arguments);
        this.state = { auth: null, signInPossible: null, clientId: "", error: null, busy: false, build: null };
        this.buttonHost = null;
    }
    async componentDidMount() {
        try {
            const auth = await fetchAuthState();
            this.setState({ auth });
            if (auth.signedIn) {
                return;
            }
        }
        catch (e) {
            this.setState({ auth: { signedIn: false }, error: e instanceof Error ? e.message : String(e) });
        }
        try {
            const config = await fetchAuthConfig();
            this.setState({ signInPossible: config.enabled, clientId: config.googleClientId });
            if (config.enabled) {
                await this.loadGis();
                this.renderGoogleButton();
            }
        }
        catch (e) {
            this.setState({ signInPossible: false, error: e instanceof Error ? e.message : String(e) });
        }
        // Cosmetic, and deliberately last: the sign-in screen names the build being signed in to,
        // which is how you tell a stale container from a fresh one without getting in.
        try {
            const response = await fetch("/api/system/build-info");
            if (response.ok)
                this.setState({ build: (await response.json()).build });
        }
        catch {
            /* a missing build stamp is not worth showing an error for */
        }
    }
    loadGis() {
        return new Promise((resolve, reject) => {
            if (typeof google !== "undefined" && google?.accounts?.id) {
                resolve();
                return;
            }
            const existing = document.querySelector(`script[src="${GIS_SRC}"]`);
            if (existing) {
                existing.addEventListener("load", () => resolve());
                existing.addEventListener("error", () => reject(new Error("Could not load Google Sign-In.")));
                return;
            }
            const script = document.createElement("script");
            script.src = GIS_SRC;
            script.async = true;
            script.defer = true;
            script.onload = () => resolve();
            script.onerror = () => reject(new Error("Could not load Google Sign-In."));
            document.head.appendChild(script);
        });
    }
    renderGoogleButton() {
        if (!this.buttonHost || !this.state.clientId) {
            return;
        }
        google.accounts.id.initialize({
            client_id: this.state.clientId,
            callback: (response) => {
                void this.onCredential(response?.credential);
            },
        });
        google.accounts.id.renderButton(this.buttonHost, {
            theme: "filled_blue",
            size: "large",
            text: "signin_with",
            shape: "rectangular",
        });
        // One Tap, as Anvil's <GoogleLogin useOneTap /> does.
        google.accounts.id.prompt();
    }
    async onCredential(credential) {
        if (!credential) {
            this.setState({ error: "Google returned no credential." });
            return;
        }
        this.setState({ busy: true, error: null });
        try {
            await signInWithGoogle(credential);
            // Re-ask rather than assume: the cookie is what decides, and reading it back proves it
            // was actually set (third-party cookie blocking would show up here, not on the POST).
            this.setState({ auth: await fetchAuthState(), busy: false });
        }
        catch (e) {
            this.setState({ error: e instanceof Error ? e.message : String(e), busy: false });
        }
    }
    render() {
        const { auth, signInPossible, error, busy, build } = this.state;
        if (auth === null) {
            return (_jsx("div", { className: "grid h-screen w-screen place-items-center bg-slate-900", children: _jsx("div", { className: "animate-pulse text-slate-500", children: "loading content editor\u2026" }) }));
        }
        if (auth.signedIn) {
            return _jsx(_Fragment, { children: this.props.children });
        }
        return (_jsx("div", { className: "flex h-screen w-screen items-center justify-center bg-slate-900 p-6 text-slate-200", children: _jsxs("div", { className: "w-full max-w-md rounded-2xl border border-slate-700 bg-slate-800 p-8 text-center shadow-xl", children: [_jsxs("div", { className: "mb-6", children: [_jsx("div", { className: "mb-2 text-4xl", children: "\uD83C\uDFA1" }), _jsx("h1", { className: "text-2xl font-semibold text-slate-100", children: "Conveyor Content Editor" }), _jsx("p", { className: "mt-2 text-sm text-slate-400", children: "Sign in with your company Google account" })] }), _jsx("div", { className: "flex justify-center", ref: (el) => { this.buttonHost = el; } }), signInPossible === false && (_jsxs("p", { className: "rounded border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-300", children: ["This server has no Google client id configured, so nobody can sign in. Set the ", _jsx("code", { className: "font-mono", children: "Auth__GoogleClientId" }), " app setting."] })), busy && _jsx("p", { className: "mt-4 text-sm text-slate-400", children: "Signing in\u2026" }), error && _jsx("p", { className: "mt-4 text-sm text-red-400", children: error }), _jsx("p", { className: "mt-6 text-xs text-slate-500", children: "Access restricted to Twist Games Workspace members." }), build && _jsxs("p", { className: "mt-2 text-xs text-slate-600", children: ["build ", build] })] }) }));
    }
}
